import type { Id } from "@supermanus/document-model";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from "react";

export interface UsePointerReorderOptions {
  readOnly?: boolean;
  /** `x` for a horizontal chip row, `y` for a vertical list, `flow` for wrapping tabs. */
  axis?: "x" | "y" | "flow";
  onMove: (id: Id, beforeId: Id | null) => void;
  /** Called on pointerup when the pointer did not move enough to count as a drag. */
  onActivate?: (id: Id) => void;
}

export interface UsePointerReorderResult {
  listRef: RefObject<HTMLElement | null>;
  dragId: Id | null;
  dropBeforeId: Id | null | undefined;
  beginDrag: (id: Id, e: ReactPointerEvent<HTMLElement>) => void;
}

function collectStepItems(root: HTMLElement): { ids: Id[]; els: HTMLElement[] } {
  const ids: Id[] = [];
  const els: HTMLElement[] = [];
  for (const el of root.querySelectorAll<HTMLElement>(":scope > [data-step-id]")) {
    const itemId = el.dataset.stepId;
    if (!itemId) continue;
    ids.push(itemId);
    els.push(el);
  }
  if (ids.length > 0) return { ids, els };
  for (const el of root.querySelectorAll<HTMLElement>("[data-step-id]")) {
    const itemId = el.dataset.stepId;
    if (!itemId) continue;
    ids.push(itemId);
    els.push(el);
  }
  return { ids, els };
}

export function arrayMoveIds(ids: Id[], from: number, to: number): Id[] {
  const next = ids.slice();
  const [item] = next.splice(from, 1);
  if (item === undefined) return ids;
  next.splice(to, 0, item);
  return next;
}

function dropBeforeFromPointer(
  root: HTMLElement,
  dragId: Id,
  ev: PointerEvent,
  axis: "x" | "y" | "flow",
): Id | null {
  const { ids, els } = collectStepItems(root);
  const from = ids.indexOf(dragId);
  if (from < 0) return null;

  let to = ids.length;
  for (let i = 0; i < els.length; i++) {
    const rect = els[i]!.getBoundingClientRect();
    if (axis === "flow") {
      if (ev.clientY < rect.top) {
        to = i;
        break;
      }
      if (
        ev.clientY <= rect.bottom &&
        ev.clientX < rect.left + rect.width / 2
      ) {
        to = i;
        break;
      }
      continue;
    }
    const pos = axis === "x" ? ev.clientX : ev.clientY;
    const start = axis === "x" ? rect.left : rect.top;
    const end = axis === "x" ? rect.right : rect.bottom;
    if (pos < start) {
      to = i;
      break;
    }
    if (pos <= end) {
      to = i;
      break;
    }
  }

  const next = arrayMoveIds(ids, from, to);
  const at = next.indexOf(dragId);
  return next[at + 1] ?? null;
}

/**
 * Pointer reorder for `[data-step-id]` children.
 * A short press without movement calls `onActivate` instead of `onMove`.
 */
export function usePointerReorder({
  readOnly,
  axis = "y",
  onMove,
  onActivate,
}: UsePointerReorderOptions): UsePointerReorderResult {
  const listRef = useRef<HTMLElement | null>(null);
  const [dragId, setDragId] = useState<Id | null>(null);
  const [dropBeforeId, setDropBeforeId] = useState<Id | null | undefined>(
    undefined,
  );
  const onMoveRef = useRef(onMove);
  const onActivateRef = useRef(onActivate);
  const readOnlyRef = useRef(readOnly);
  const axisRef = useRef(axis);
  const endDragRef = useRef<(() => void) | null>(null);
  onMoveRef.current = onMove;
  onActivateRef.current = onActivate;
  readOnlyRef.current = readOnly;
  axisRef.current = axis;

  useEffect(() => {
    return () => {
      endDragRef.current?.();
      document.body.classList.remove("sm-is-step-dragging");
    };
  }, []);

  const beginDrag = useCallback((id: Id, e: ReactPointerEvent<HTMLElement>) => {
    if (e.button !== 0) return;
    if (readOnlyRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    const pointerId = e.pointerId;
    endDragRef.current?.();
    let latestBefore: Id | null = null;
    let moved = false;
    const startX = e.clientX;
    const startY = e.clientY;
    let finished = false;

    const onMovePtr = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      if (
        !moved &&
        Math.abs(ev.clientX - startX) <= 5 &&
        Math.abs(ev.clientY - startY) <= 5
      ) {
        return;
      }
      if (!moved) {
        moved = true;
        document.body.classList.add("sm-is-step-dragging");
        setDragId(id);
      }
      const root = listRef.current;
      if (!root) return;
      const before = dropBeforeFromPointer(
        root,
        id,
        ev,
        axisRef.current ?? "y",
      );
      latestBefore = before;
      setDropBeforeId(before);
    };

    const finish = (commit: boolean) => {
      if (finished) return;
      finished = true;
      window.removeEventListener("pointermove", onMovePtr);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
      endDragRef.current = null;
      document.body.classList.remove("sm-is-step-dragging");
      if (commit && moved) onMoveRef.current(id, latestBefore);
      else if (commit && !moved) onActivateRef.current?.(id);
      setDragId(null);
      setDropBeforeId(undefined);
    };

    const onUp = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      finish(true);
    };
    const onCancel = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      // Re-render during drag can cancel the pointer. Keep the move.
      finish(moved);
    };

    window.addEventListener("pointermove", onMovePtr);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
    endDragRef.current = () => finish(moved);
  }, []);

  return { listRef, dragId, dropBeforeId, beginDrag };
}

export function reorderByBefore<T extends { id: Id }>(
  items: T[],
  id: Id,
  beforeId: Id | null,
): T[] {
  const moving = items.find((s) => s.id === id);
  if (!moving) return items;
  const rest = items.filter((s) => s.id !== id);
  if (beforeId == null) return [...rest, moving];
  const index = rest.findIndex((s) => s.id === beforeId);
  if (index < 0) return [...rest, moving];
  const next = [...rest];
  next.splice(index, 0, moving);
  return next;
}
